import { useState } from 'react'
import { Clock, Users, Video, Zap, MapPin, Phone, CheckCircle2, ArrowRight } from 'lucide-react'

export default function MeetingSchedulerPage() {
  const [stats] = useState({
    meetingsScheduled: 847,
    attendees: 3456,
    avgDuration: '45 min',
    roomsAvailable: 24,
  })

  const features = [
    { title: '⏰ Smart Scheduling', description: 'AI-powered meeting scheduling with time conflicts check', icon: Clock, color: 'from-blue-600 to-blue-500', stats: 'AI Optimized' },
    { title: '📱 Multiple Participants', description: 'Invite multiple attendees with availability check', icon: Users, color: 'from-green-600 to-green-500', stats: 'Unlimited' },
    { title: '🎥 Video Integration', description: 'One-click Zoom, Teams, Google Meet, WebEx', icon: Video, color: 'from-purple-600 to-purple-500', stats: '4 Platforms' },
    { title: '📍 Room Booking', description: 'Automatic conference room and resource booking', icon: MapPin, color: 'from-red-600 to-red-500', stats: '24 Rooms' },
    { title: '📞 Audio Bridges', description: 'Dial-in numbers for remote participants', icon: Phone, color: 'from-cyan-600 to-cyan-500', stats: 'Global Numbers' },
    { title: '⚡ Instant Reschedule', description: 'One-click rescheduling with notifications', icon: Zap, color: 'from-yellow-600 to-yellow-500', stats: 'Instant' },
    { title: '✓ Confirmations', description: 'Automatic send calendar invites and reminders', icon: CheckCircle2, color: 'from-teal-600 to-teal-500', stats: '100% Sent' },
    { title: '📊 Analytics', description: 'Meeting analytics and attendance tracking', icon: Zap, color: 'from-indigo-600 to-indigo-500', stats: 'Real-time' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 p-8 text-white shadow-2xl border border-cyan-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Clock className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Meeting Scheduler</h1>
                </div>
                <p className="text-cyan-100 text-lg font-semibold">Intelligent Meeting Scheduling & Management</p>
                <p className="text-cyan-200 text-sm mt-1">Smart Scheduling • Video Integration • Room Booking • Automatic Notifications</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.meetingsScheduled}</div>
                <div className="text-sm text-cyan-100">Meetings Scheduled</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Meetings Scheduled', value: stats.meetingsScheduled, icon: Clock, color: 'from-blue-600 to-blue-500' },
            { label: 'Total Attendees', value: `${stats.attendees} +`, icon: Users, color: 'from-green-600 to-green-500' },
            { label: 'Avg Duration', value: stats.avgDuration, icon: Video, color: 'from-purple-600 to-purple-500' },
            { label: 'Available Rooms', value: stats.roomsAvailable, icon: MapPin, color: 'from-red-600 to-red-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Meeting Scheduling Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div key={idx} className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1">
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>
                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
